<template>
  <a-dialog
    ref="dialog"
    name="subscribe_to_plan"
    :loading="loading ? loadingText : ''"
    @open="opened"
    @close="$reset"
    :actions="false"
    :dense="true"
    :persistent="true"
    :overlay-opacity="0.8"
  >
    <span slot="title">{{ $t('title') }}</span>
    <div slot="content">
      <v-stepper v-model="steps" vertical style="box-shadow: none;">
        <v-stepper-step :complete="steps > 1" step="1">{{ $t('billing-details') }}</v-stepper-step>
        <v-stepper-content step="1">
          <a-customer-form
            ref="informations"
            @valid="onInformationValid"
            @message="onFormMessage">
          </a-customer-form>

          <div class="mt-2 text-right">
            <v-btn text color="grey" @click="$refs.dialog.close()" class="mr-2">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn
              color="success"
              @click="submitInformation()"
              :disabled="!forms.informations.valid || loading">
              {{ $t('actions.next') }}
            </v-btn>
          </div>
        </v-stepper-content>

        <v-stepper-step :complete="steps > 2" step="2">{{ $t('validate-order') }}</v-stepper-step>
        <v-stepper-content step="2">
          <div v-if="estimate">
            <a-payment-method-form
              ref="payment_method"
              @valid="onPaymentValid"
              @message="onFormMessage"
            ></a-payment-method-form>

            <v-text-field
              :loading="forms.payment.loading"
              :label="$t('discount-code')"
              v-model="forms.payment.coupon"
              persistent-hint
              :hint="forms.payment.couponSuccess"
              :error="forms.payment.couponError"
              :error-messages="forms.payment.couponError"
            >
            </v-text-field>

            <div v-if="estimate.tax > 0" class="mt-4 blue--text text--darken-2">
              <v-icon left small color="blue darken-2">mdi-lightbulb-on-outline</v-icon>
              <span v-html="$t('tax-added')"></span>
            </div>

            <div class="mt-4 text-right">
              <v-btn color="grey" class="mr-2" text @click="steps = 1">{{ $t('actions.cancel') }}</v-btn>
              <v-btn color="success" @click="submitPayment()" :disabled="forms.payment.loading || forms.payment.couponError">
                <v-icon left>mdi-lock</v-icon>
                {{ $t('actions.pay') }} {{ $helpers.present.currency(estimate.total / 100) }} {{ estimate.tax > 0 ? $t('prices.with-taxes') : $t('prices.without-taxes') }}
              </v-btn>
            </div>
          </div>
        </v-stepper-content>
      </v-stepper>
    </div>
  </a-dialog>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'SubscribeToPlanDialog',
  data () {
    return {
      loading: false,
      loadingText: '',
      steps: 1,
      subscription: null,
      estimate: null,
      forms: {
        informations: {
          valid: false,
        },
        payment: {
          valid: false,
          loading: false,
          coupon: null,
          couponSuccess: this.$t('discount-input'),
          couponError: null
        },
      }
    }
  },
  watch: {
    'forms.payment.coupon': debounce(function (coupon) {
      this.forms.payment.loading = true
      if (coupon === '') {
        this.subscription.applyCouponCode('')
        this.forms.payment.couponSuccess =  this.$t('discount-input')
        this.forms.payment.couponError = null
        this.loadEstimate().finally(() => {
          this.forms.payment.loading = false
        })
      } else {
        this.subscription.applyCouponCode(coupon)
          .then(message => {
            this.forms.payment.couponSuccess = message
            this.forms.payment.couponError = null
            this.loadEstimate().finally(() => {
              this.forms.payment.loading = false
            })
          })
          .catch(message => {
            this.forms.payment.loading = false
            this.forms.payment.couponSuccess =  this.$t('discount-input')
            this.forms.payment.couponError = message
          })
      }
    }, 500),
    steps (value) {
      if (value === 2) {
        this.loading = true
        this.loadingText = this.$t('prices-loading')
        this.loadEstimate().finally(() => {
          this.loading = false
        })
      }
    }
  },
  methods: {
    // INFORMATIONS FORM
    onInformationValid (valid) {
      this.forms.informations.valid = valid
    },
    submitInformation () {
      this.loading = true
      this.steps = 0
      this.$refs.informations.submit()
        .then(() => {
          this.loading = false
          this.steps = 2
        })
        .catch(err => {
          this.loading = false
          this.$ui.error(err)
          this.steps = 1
        })
    },

    // PAYMENT METHOD FORM
    onPaymentValid (valid) {
      this.forms.payment.valid = valid
    },
    submitPayment () {
      this.loading = true

      this.loadingText = this.$t('create-order')

      // If the estimate is free, try to save the card instead of paying with
      if(this.estimate.total === 0) {
        this.$refs.payment_method.save()
            .then(() => {
              // And then, when we have a default payment for this account, try to create a subscription with this
              this.subscription.create(true)
                  .then(response => {
                    if (response.payment_intent) {
                      this.submitPaymentWithPaymentIntent(response.payment_intent)
                    } else if (response.incomplete_payment) {
                      this.validateIncompletePayment(response.incomplete_payment)
                    } else if (response.payment_completed) {
                      this.refreshAccountInformations().then(() => {
                        this.closeDialogWithSuccess(this.$t('payment-success'))
                      })
                    } else {
                      this.loading = false
                      this.$ui.error(this.$t('error-warning'))
                    }
                  })
                  .catch(err => {
                    this.loading = false
                    this.$ui.error(err)
                  })
            })
            .catch(err => {
              this.loading = false
              this.$ui.error(err)
            })
      } else {
        this.subscription.create(this.$refs.payment_method.useDefaultPaymentMethod)
            .then(response => {
              if (response.payment_intent) {
                this.submitPaymentWithPaymentIntent(response.payment_intent)
              } else if (response.incomplete_payment) {
                this.validateIncompletePayment(response.incomplete_payment)
              } else if (response.payment_completed) {
                this.refreshAccountInformations().then(() => {
                  this.closeDialogWithSuccess(this.$t('payment-success'))
                })
              } else {
                this.loading = false
                this.$ui.error(this.$t('error-warning'))
              }
            })
            .catch(err => {
              this.loading = false
              this.$ui.error(err)
            })
      }
    },

    /*
     * COMMON ELEMENTS FOR PAYMENT MANAGMENT
     */

    // Payment response handlers
    submitPaymentWithPaymentIntent (paymentIntent) {
      this.$refs.payment_method.pay(paymentIntent)
        .then(() => this.waitForSubscriptionAndCloseDialog())
        .catch(err => {
          this.loading = false
          this.$ui.error(err)
        })
    },
    validateIncompletePayment (incompletePayment) {
      this.$refs.payment_method.pay(incompletePayment.client_secret, incompletePayment.payment_method)
        .then(() => this.waitForSubscriptionAndCloseDialog())
        .catch(err => {
          this.refreshAccountInformations()
            .then(() => this.closeDialogWithError(err))
        })
    },

    // Dialog control / action outcome control
    closeDialogWithSuccess (message) {
      this.$ui.success(message)
      this.loading = false
      this.closeDialog()
    },
    closeDialogWithError (message) {
      this.$ui.warning(message)
      this.loading = false
      this.closeDialog()
    },
    closeDialogWithTimeout () {
      this.$ui.warning(this.$t('waiting-payment'))
      this.loading = false
      this.closeDialog()
    },
    closeDialog () {
      if (this.$refs.dialog) this.$refs.dialog.close()
    },

    // Helpers
    refreshAccountInformations () {
      this.loadingText = this.$t('subscription-update')
      return new Promise(resolve => {
        this.$account.fetch({ showLoading: false }).then(() => resolve())
      })
    },
    waitForSubscriptionAndCloseDialog () {
      this.loadingText = this.$t('waiting-payment-and-close')
      this.waitForSubscription()
        .then(success => {
          if (success) this.closeDialogWithSuccess(this.$t('payment-success'))
          else this.closeDialogWithTimeout()
        })
    },
    waitForSubscription () {
      return new Promise(resolve => {
        let i = 0

        const interval = setInterval(() => {
          if (i++ >= 4) {
            clearInterval(interval)
            resolve(false)
          } else {
            this.$account.fetch({ showLoading: false })
              .then(() => {
                const subscription = this.$account.getSubscription('check-position')
                if (subscription.name !== 'default' && subscription.status === 'active') {
                  clearInterval(interval)
                  resolve(true)
                }
              })
              .catch(() => {
                clearInterval(interval)
                resolve(false)
              })
          }
        }, 5 * 1000)
      })
    },

    opened (subscription) {
      this.subscription = subscription
      if (this.$account.current.customer) {
        this.steps = 2
      }
    },
    onFormMessage (message) {
      this.loadingText = message
    },
    loadEstimate () {
      return this.$api.post('account/customer/subscription/estimate', {
        with_payment_intent: true,
        ...this.subscription.toArray()
      }).then(response => this.estimate = response.data)
    },
  }
}
</script>

<style scoped>
.v-application--is-ltr .v-stepper--vertical .v-stepper__content {
  margin-left: 0;
}
</style>

<i18n>
{
	"en": {
		"tax-added": "A 20% VAT is applied to your account. If you are a company outside France with an Intra-Community VAT number <a href=\"mailto:hello@check-position.com\">contact us</a> specifying your country and your Intracom VAT number.",
		"billing-details": "Billing information",
		"create-order": "Creating your order ...",
		"discount-code": "Discount code",
		"discount-input": "If you have a discount code, enter it here before validating your order.",
		"error-warning": "An unknown error has occurred. Please try again in a few moments.",
		"payment-success": "Payment successfully validated!",
		"prices-loading": "Loading pricing ...",
		"subscription-update": "Updating your subscription ...",
		"title": "Subscribe to an offer",
		"validate-order": "Validate the order",
		"waiting-payment": "Payment is still pending, please wait a few minutes then refresh the page.",
		"waiting-payment-and-close": "Waiting for payment (please wait, it may take several seconds) ..."
	},
	"es": {
		"tax-added": "Se aplica un 20% de IVA a su cuenta. Si es una empresa fuera de Francia con un número de IVA intracomunitario <a href=\"mailto:hello@check-position.com\">contáctenos</a> especificando su país y su número de IVA intracomunitario.",
		"billing-details": "Información de facturación",
		"create-order": "Creando su pedido ...",
		"discount-code": "Código de descuento",
		"discount-input": "Si tiene un código de descuento, ingréselo aquí antes de validar su pedido.",
		"error-warning": "Un error desconocido a ocurrido. Vuelva a intentarlo en unos momentos.",
		"payment-success": "Pago validado con éxito!",
		"prices-loading": "Cargando tarifas ...",
		"subscription-update": "Actualizando su suscripción ...",
		"title": "Suscríbete a una oferta",
		"validate-order": "Validar el pedido",
		"waiting-payment": "El pago aún está pendiente, espere unos minutos y luego actualice la página.",
		"waiting-payment-and-close": "Esperando el pago (espere, puede tardar varios segundos) ..."
	},
	"fr": {
		"tax-added": "Une TVA de 20% est appliquée sur votre compte. Si vous êtes une entreprise hors France disposant d'un numéro de TVA Intracommunautaire <a href=\"mailto:bonjour@check-position.com\">contactez nous</a> en spécifiant votre pays et votre numéro de TVA Intracom.",
		"billing-details": "Coordonnées de facturation",
		"create-order": "Création de votre commande ...",
		"discount-code": "Code de réduction",
		"discount-input": "Si vous avez un code de réduction, saisissez-le ici avant de valider votre commande.",
		"error-warning": "Une erreur inconnue est survenue. Merci de tenter à nouveau dans quelques instants.",
		"payment-success": "Paiement validé avec succès !",
		"prices-loading": "Chargement des tarifs ...",
		"subscription-update": "Mise à jour de votre souscription ...",
		"title": "Souscrire à une offre",
		"validate-order": "Valider la commande",
		"waiting-payment": "Le paiement est toujours en attente, merci de patienter quelques minutes puis actualiser la page.",
		"waiting-payment-and-close": "En attente du paiement (merci de patienter, cela peut prendre plusieurs secondes) ..."
	},
	"it": {
		"tax-added": "Sul tuo account viene applicata un'IVA del 20%. Se sei un'azienda fuori dalla Francia con un numero di partita IVA intracomunitaria <a href=\"mailto:hello@check-position.com\">contattaci</a> specificando il tuo paese e il tuo numero IVA Intracom.",
		"billing-details": "Informazioni di fatturazione",
		"create-order": "Creazione del tuo ordine ...",
		"discount-code": "Codice sconto",
		"discount-input": "Se hai un codice sconto, inseriscilo qui prima di convalidare il tuo ordine.",
		"error-warning": "Si è verificato un errore sconosciuto. Riprova tra qualche momento.",
		"payment-success": "Pagamento convalidato con successo!",
		"prices-loading": "Caricamento prezzi ...",
		"subscription-update": "Aggiornamento del tuo abbonamento ...",
		"title": "Abbonati a un'offerta",
		"validate-order": "Convalida l'ordine",
		"waiting-payment": "Il pagamento è ancora in attesa, attendi qualche minuto e poi aggiorna la pagina.",
		"waiting-payment-and-close": "In attesa del pagamento (attendi, potrebbe richiedere qualche secondo) ..."
	},
	"pt": {
		"tax-added": "É aplicado um IVA de 20% à sua conta. Se for uma empresa fora de França com um número de IVA intracomunitário <a href=\"mailto:hello@check-position.com\">contacte-nos</a> especificando o seu país e o seu número de IVA Intracom.",
		"billing-details": "Informações de faturação",
		"create-order": "A criar o seu pedido ...",
		"discount-code": "Código de desconto",
		"discount-input": "Se tiver um código de desconto, introduza-o aqui antes de validar o seu pedido.",
		"error-warning": "Ocorreu um erro desconhecido. Por favor tente novamente em alguns instantes.",
		"payment-success": "Pagamento validado com sucesso!",
		"prices-loading": "A carregar tarifas ...",
		"subscription-update": "A atualizar a sua subscrição ...",
		"title": "Subscrever uma oferta",
		"validate-order": "Validar o pedido",
		"waiting-payment": "O pagamento ainda está pendente, por favor aguarde alguns minutos e depois atualize a página.",
		"waiting-payment-and-close": "À espera do pagamento (por favor aguarde, pode demorar alguns segundos) ..."
	},
	"pl": {
		"tax-added": "Do Twojego konta stosowany jest 20% podatek VAT. Jeśli jesteś firmą spoza Francji z numerem VAT wewnątrzwspólnotowym <a href=\"mailto:hello@check-position.com\">skontaktuj się z nami</a> podając swój kraj i numer VAT Intracom.",
		"billing-details": "Dane do faktury",
		"create-order": "Tworzenie Twojego zamówienia ...",
		"discount-code": "Kod rabatowy",
		"discount-input": "Jeśli masz kod rabatowy, wpisz go tutaj przed zatwierdzeniem zamówienia.",
		"error-warning": "Wystąpił nieznany błąd. Spróbuj ponownie za chwilę.",
		"payment-success": "Płatność pomyślnie zatwierdzona!",
		"prices-loading": "Ładowanie cennika ...",
		"subscription-update": "Aktualizacja Twojej subskrypcji ...",
		"title": "Subskrybuj ofertę",
		"validate-order": "Zatwierdź zamówienie",
		"waiting-payment": "Płatność jest nadal oczekująca, poczekaj kilka minut, a następnie odśwież stronę.",
		"waiting-payment-and-close": "Oczekiwanie na płatność (prosimy o cierpliwość, może to potrwać kilka sekund) ..."
	}
}
</i18n>
